const stripQueryAndHash = (value: string): string => value.split('?')[0].split('#')[0]

/**
 * 절대 URL 또는 path 문자열을 host 를 제거한 path 로 정규화한다.
 * - trailing slash 는 원본 그대로 보존한다 (DAV collection URL 은 관례상 '/' 로 끝난다)
 * - query string 과 hash 는 제거한다
 * - 이미 path 인 문자열은 trim 만 하고 그대로 반환한다
 * - 빈 값·비문자열은 '' 반환
 * - percent-encoding 은 디코딩하지 않는다 (이중 디코딩 위험). 인코딩 표기가 다른 경우 미스매치가 남을 수 있다.
 */
export const toDavPath = (url?: string | null): string => {
  if (typeof url !== 'string') {
    return ''
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return ''
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).pathname
    } catch {
      return trimmed
    }
  }

  return stripQueryAndHash(trimmed)
}

/**
 * 비교 전용 정규형. path 로 변환한 뒤 trailing slash 를 제거한다.
 */
export const davKey = (url?: string | null): string => {
  const path = toDavPath(url)
  if (!path) {
    return ''
  }

  if (path === '/') {
    return '/'
  }

  return path.slice(-1) === '/' ? path.slice(0, -1) : path
}

/**
 * 두 값이 같은 DAV collection 을 가리키는지 host 무시하고 판정한다.
 */
export const isSameDavUrl = (a?: string | null, b?: string | null): boolean => {
  const keyA = davKey(a)
  const keyB = davKey(b)
  if (!keyA || !keyB) {
    return false
  }

  return keyA === keyB
}

/**
 * 문자열 배열에 해당 URL 이 host 무시하고 포함되는지 판정한다.
 */
export const davListIncludes = (list?: readonly string[] | null, url?: string | null): boolean => {
  if (!Array.isArray(list)) {
    return false
  }

  const key = davKey(url)
  if (!key) {
    return false
  }

  return list.some((item) => davKey(item) === key)
}

/**
 * path 또는 절대 URL 을 base 기준 절대 URL 로 복원한다.
 * 이미 절대 URL 이어도 base 의 host 로 치환된다.
 */
export const toDavUrl = (pathOrUrl?: string | null, base?: string | null): string => {
  const path = toDavPath(pathOrUrl)
  if (!path) {
    return ''
  }

  if (!base) {
    return path
  }

  try {
    return new URL(path, base).href
  } catch {
    return path
  }
}

/**
 * 문자열 배열을 path 배열로 정규화한다. 빈 값 제거 + davKey 기준 중복 제거(선행 항목 우선).
 */
export const toDavPathList = (list?: readonly string[] | null): string[] => {
  if (!Array.isArray(list)) {
    return []
  }

  const seen = new Set<string>()
  const result: string[] = []

  list.forEach((item) => {
    const path = toDavPath(item)
    if (!path) {
      return
    }

    const key = davKey(path)
    if (seen.has(key)) {
      return
    }

    seen.add(key)
    result.push(path)
  })

  return result
}
